import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getFeatureFlags, type FeatureFlags } from './featureFlags';

type FeatureFlagsContextValue = {
  flags: FeatureFlags;
  /** Query string sans « ? », à repasser sur les navigations internes. */
  flagSearch: string;
};

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | null>(null);

export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const [searchParams] = useSearchParams();
  const value = useMemo(() => {
    const flagSearch = searchParams.toString();
    return {
      flags: getFeatureFlags(flagSearch ? `?${flagSearch}` : ''),
      flagSearch,
    };
  }, [searchParams]);

  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
}

function useFeatureFlagsContext(): FeatureFlagsContextValue {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error('useFeatureFlags doit être utilisé sous FeatureFlagsProvider');
  }
  return context;
}

export function useFeatureFlags(): FeatureFlags {
  return useFeatureFlagsContext().flags;
}

export function useFlagSearch(): string {
  return useFeatureFlagsContext().flagSearch;
}
